import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "@/lib/router";
import { authApi } from "../api/auth";
import { healthApi } from "../api/health";
import { queryKeys } from "../lib/queryKeys";
import { Button } from "@/components/ui/button";

async function initiateSSO(nextPath: string): Promise<{ url: string } | { error: string }> {
  let res: Response;
  try {
    res = await fetch("/api/auth/sign-in/oauth2", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        providerId: "cognito",
        callbackURL: window.location.origin + (nextPath || "/"),
      }),
    });
  } catch (err) {
    return { error: `Network error: ${err instanceof Error ? err.message : String(err)}` };
  }

  let data: unknown;
  try {
    data = await res.json();
  } catch {
    return { error: `SSO request failed (${res.status}): non-JSON response` };
  }

  if (!res.ok) {
    const msg =
      (data as { message?: string })?.message ??
      (data as { error?: string })?.error ??
      `SSO request failed with status ${res.status}`;
    return { error: msg };
  }

  const rawUrl = (data as { url?: string }).url ?? null;
  if (!rawUrl) {
    return { error: `SSO response missing redirect URL (got: ${JSON.stringify(data)})` };
  }

  // Skip the Cognito hosted UI and go straight to Google sign-in
  const sep = rawUrl.includes("?") ? "&" : "?";
  return { url: `${rawUrl}${sep}identity_provider=Google` };
}

export function AuthPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const nextPath = searchParams.get("next") || "/";
  const { data: session, isLoading: isSessionLoading } = useQuery({
    queryKey: queryKeys.auth.session,
    queryFn: () => authApi.getSession(),
    retry: false,
  });
  const { data: health, isLoading: isHealthLoading } = useQuery({
    queryKey: queryKeys.health,
    queryFn: () => healthApi.get(),
    retry: false,
  });

  // Redirect to app if already signed in
  useEffect(() => {
    if (session) {
      navigate(nextPath, { replace: true });
    }
  }, [session, navigate, nextPath]);

  // Auto-redirect to SSO when configured (skip the email/password form entirely)
  const [ssoRedirecting, setSsoRedirecting] = useState(false);
  const [ssoFailed, setSsoFailed] = useState(false);
  useEffect(() => {
    if (
      health?.ssoEnabled &&
      !session &&
      !isSessionLoading &&
      !isHealthLoading &&
      !ssoRedirecting &&
      !ssoFailed &&
      !searchParams.get("manual") // escape hatch: ?manual=1 shows the form
    ) {
      setSsoRedirecting(true);
      initiateSSO(nextPath).then((result) => {
        if ("url" in result) {
          window.location.href = result.url;
        } else {
          // SSO initiation failed — fall back to manual form
          console.error("[SSO] Auto-redirect failed:", result.error);
          setSsoRedirecting(false);
          setSsoFailed(true);
        }
      });
    }
  }, [health, session, isSessionLoading, isHealthLoading, ssoRedirecting, ssoFailed, nextPath, searchParams]);

  const mutation = useMutation({
    mutationFn: async () => {
      await authApi.signInEmail({ email: email.trim(), password });
    },
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: queryKeys.auth.session });
      await queryClient.invalidateQueries({ queryKey: queryKeys.companies.all });
      navigate(nextPath, { replace: true });
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Authentication failed");
    },
  });

  const canSubmit = email.trim().length > 0 && password.trim().length >= 8;

  // Show loading while checking session/health or redirecting to SSO
  if (isSessionLoading || isHealthLoading || ssoRedirecting) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <img
            src="/brands/ligament-icon.png"
            alt="Ligament"
            className="h-20 w-20 object-contain"
          />
          <p className="text-sm text-muted-foreground">
            {ssoRedirecting ? "Redirecting to sign in..." : "Loading..."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-gray-50 overflow-y-auto">
      <div className="w-full max-w-md mx-auto px-6 py-12">
        {/* Branding */}
        <div className="flex flex-col items-center mb-8">
          <img
            src="/brands/ligament-icon.png"
            alt="Ligament"
            className="h-14 object-contain mb-3"
          />
          <div className="flex items-center gap-1.5 text-[10px] text-gray-400 uppercase tracking-wider">
            <span>Powered by</span>
            <img
              src="/brands/medwork-logo.svg"
              alt="MEDWORK.iO"
              className="h-4 object-contain"
            />
          </div>
        </div>

        {/* Heading */}
        <h1 className="text-xl font-semibold text-center text-gray-600">
          Sign in to your account
        </h1>

        {/* Card */}
        <div className="mt-6 bg-white rounded-xl shadow-md p-6">
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              mutation.mutate();
            }}
          >
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                Email address
              </label>
              <input
                className="w-full rounded-lg border border-gray-300 bg-white px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-muted-foreground/60"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                autoFocus
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-medium text-gray-700">Password</label>
                <button
                  type="button"
                  className="text-sm font-medium text-blue-600 hover:text-blue-500 transition-colors"
                  onClick={() => {
                    // Placeholder — wire to better-auth password reset later
                  }}
                >
                  Forgot password?
                </button>
              </div>
              <input
                className="w-full rounded-lg border border-gray-300 bg-white px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-muted-foreground/60"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
              />
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <Button
              type="submit"
              disabled={!canSubmit || mutation.isPending}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-lg text-sm font-medium"
            >
              {mutation.isPending ? "Signing in..." : "Sign in"}
            </Button>
          </form>

          {/* SSO section — only shown when Cognito SSO is configured */}
          {health?.ssoEnabled && (
            <>
              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-3 text-muted-foreground">Or continue with</span>
                </div>
              </div>

              <button
                type="button"
                className="w-full flex items-center justify-center gap-3 rounded-lg border border-gray-300 bg-white px-3.5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                onClick={async () => {
                  const result = await initiateSSO(nextPath);
                  if ("url" in result) {
                    window.location.href = result.url;
                  } else {
                    console.error("[SSO] Manual sign-in failed:", result.error);
                    setError(result.error);
                  }
                }}
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                Sign in with Google
              </button>
            </>
          )}
        </div>

        {/* Footer */}
        <p className="mt-6 text-sm text-muted-foreground text-center">
          Having trouble signing in?{" "}
          <a
            href="mailto:support@medwork.io"
            className="font-medium text-blue-600 hover:text-blue-500 transition-colors"
          >
            Contact Support
          </a>
        </p>
      </div>
    </div>
  );
}
